import { Fragment, useState } from 'react';
import { ChevronDown, ChevronRight, ScrollText } from 'lucide-react';
import { useAuditLogs } from '@/api/audit';
import { useAllUsers } from '@/api/users';
import { Badge, Button, Card, EmptyState, ErrorState, PageHeader, Select, Spinner } from '@/components/ui';
import { formatDateTime } from '@/lib/format';

const ENTITY_TYPES = ['user', 'team'];

export function AuditLogPage() {
  const [entityType, setEntityType] = useState('');
  const [open, setOpen] = useState<string | null>(null);
  const logs = useAuditLogs({ entityType: entityType || undefined });
  const users = useAllUsers();
  const nameOf = (id: string | null) =>
    id ? (users.data?.find((u) => u.id === id)?.name ?? id.slice(0, 8)) : 'System';
  const rows = logs.data?.pages.flatMap((p) => p.data) ?? [];

  return (
    <>
      <PageHeader title="Audit log" description="Every admin and security-relevant action, newest first." />
      <Card>
        <div className="border-b border-slate-100 p-4">
          <Select
            aria-label="Filter by entity"
            value={entityType}
            onChange={(e) => setEntityType(e.target.value)}
            wrapperClassName="sm:w-48"
          >
            <option value="">All entities</option>
            {ENTITY_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </Select>
        </div>
        {logs.isPending ? (
          <div className="flex justify-center py-16">
            <Spinner size="lg" label="Loading audit log" />
          </div>
        ) : logs.error ? (
          <ErrorState error={logs.error} onRetry={() => void logs.refetch()} />
        ) : rows.length === 0 ? (
          <EmptyState icon={<ScrollText className="h-6 w-6" />} title="Nothing logged yet" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] text-left text-sm">
              <thead className="text-xs font-semibold tracking-wide text-slate-500 uppercase">
                <tr className="border-b border-slate-100">
                  <th scope="col" className="w-8 px-4 py-3">
                    <span className="sr-only">Expand</span>
                  </th>
                  <th scope="col" className="px-4 py-3">
                    When (IST)
                  </th>
                  <th scope="col" className="px-4 py-3">
                    Who
                  </th>
                  <th scope="col" className="px-4 py-3">
                    Action
                  </th>
                  <th scope="col" className="px-4 py-3">
                    Target
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((r) => {
                  const hasDetail = !!(r.changes || r.meta);
                  const expanded = open === r.id;
                  return (
                    <Fragment key={r.id}>
                      <tr className="align-top">
                        <td className="px-4 py-3">
                          {hasDetail && (
                            <button
                              type="button"
                              onClick={() => setOpen(expanded ? null : r.id)}
                              aria-expanded={expanded}
                              aria-label="Show details"
                              className="rounded p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                            >
                              {expanded ? (
                                <ChevronDown className="h-4 w-4" />
                              ) : (
                                <ChevronRight className="h-4 w-4" />
                              )}
                            </button>
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-slate-600">
                          {formatDateTime(r.createdAt)}
                        </td>
                        <td className="px-4 py-3">
                          <span className="font-medium text-slate-800">{nameOf(r.actorId)}</span>
                          {r.ip && <span className="block font-mono text-[11px] text-slate-400">{r.ip}</span>}
                        </td>
                        <td className="px-4 py-3">
                          <Badge tone="brand">{r.action}</Badge>
                        </td>
                        <td className="px-4 py-3 text-slate-600">
                          {r.entityType}
                          {r.entityType === 'user' && r.entityId ? ` · ${nameOf(r.entityId)}` : ''}
                        </td>
                      </tr>
                      {expanded && (
                        <tr>
                          <td />
                          <td colSpan={4} className="px-4 pb-4">
                            <pre className="overflow-x-auto rounded-lg bg-slate-50 p-3 text-xs text-slate-700">
                              {JSON.stringify(
                                { changes: r.changes ?? undefined, meta: r.meta ?? undefined },
                                null,
                                2,
                              )}
                            </pre>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
            {logs.hasNextPage && (
              <div className="flex justify-center border-t border-slate-100 p-3">
                <Button
                  variant="ghost"
                  size="sm"
                  loading={logs.isFetchingNextPage}
                  onClick={() => void logs.fetchNextPage()}
                >
                  Load more
                </Button>
              </div>
            )}
          </div>
        )}
      </Card>
    </>
  );
}
